import type { MusicAnalytics } from "@/api/__generated__/models";
import { useAnalyticsMonth } from "@/routes/custom-music/dsp-analytics/context/dsp-analytics-context";
import { formatCurrency, formatNumber, formatPercentage } from "@/utils/format";
import {
  Button,
  Icon,
  NumberedTableHeaderRow,
  NumberedTableRow,
  ShadowBox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  Typography,
} from "@backpac/monica";
import { useMemo } from "react";
import { Link } from "react-router";

interface RevenueNumberedTableProps<T> {
  title: string;
  tab: string;
  analytics: T[];
  renderNameComponent?: (item: T) => React.ReactNode;
}

export const RevenueNumberedTable = <T extends MusicAnalytics>(props: RevenueNumberedTableProps<T>) => {
  return (
    <ShadowBox className="flex flex-col bg-white">
      <RevenueNumberedTable.Header {...props} />
      <RevenueNumberedTable.Table {...props} />
    </ShadowBox>
  );
};

RevenueNumberedTable.Header = <T extends MusicAnalytics>({ title, tab }: RevenueNumberedTableProps<T>) => {
  const { formatedMonthRange } = useAnalyticsMonth();
  return (
    <div className="flex flex-row justify-between items-center p-6 pb-2">
      <div className="flex flex-col gap-1.5">
        <Typography size="md" font="unbounded" color="textPrimary">
          {title}
        </Typography>
        <Typography size="xs" color="textMuted">
          {formatedMonthRange}
        </Typography>
      </div>
      <Link to={`${window.location.pathname}?tab=${tab}`}>
        <Button variant="outlined" size="sm" rightAddon={<Icon name="chevron-right" iconSize="12px" />}>
          See more
        </Button>
      </Link>
    </div>
  );
};

RevenueNumberedTable.Table = <T extends MusicAnalytics>({
  analytics,
  renderNameComponent,
  title,
}: RevenueNumberedTableProps<T>) => {
  const analyticsWithShare = useMemo(() => {
    const totalRevenue = analytics.reduce((acc, curr) => acc + curr.revenue, 0);
    return analytics.map((item) => ({
      ...item,
      share: (item.revenue / totalRevenue) * 100,
    }));
  }, [analytics]);

  return (
    <Table hasBoxShadow={false} isRounded={false}>
      <TableHeader>
        <NumberedTableHeaderRow>
          <TableHead>{title}</TableHead>
          <TableHead>Share</TableHead>
          <TableHead>Revenue</TableHead>
          <TableHead>Stream</TableHead>
        </NumberedTableHeaderRow>
      </TableHeader>
      <TableBody>
        {analyticsWithShare.map((item, index) => (
          <NumberedTableRow key={item.name} index={index + 1}>
            <TableCell>
              <Typography size="xs" color="textPrimary">
                {renderNameComponent ? renderNameComponent(item) : item.name}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography size="xs" color="textPrimary">
                {formatPercentage(item.share)}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography size="xs" color="textPrimary">
                {formatCurrency(item.revenue)}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography size="xs" color="textPrimary">
                {formatNumber(item.streams, 0)}
              </Typography>
            </TableCell>
          </NumberedTableRow>
        ))}
      </TableBody>
    </Table>
  );
};
